import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Combobox } from '@opendoor/atoms/combobox';
import type { ComboboxOption } from '@opendoor/atoms/combobox';
import { Text } from '@opendoor/atoms/text';
import { MapLocationSearch } from 'components/marketplace/map/LocationSearch';
import type { LocationSearchProps } from 'components/marketplace/map/LocationSearch';
import type { SavedSearchFieldsFragment } from 'components/marketplace/savedSearches/__generated__/saved-searches.zeus';
import { SavedSearchesDropdown } from 'components/marketplace/savedSearches/SavedSearchesDropdown';
import { useSavedSearches } from 'components/marketplace/savedSearches/useSavedSearches';

interface QuickFiltersProps {
  bathrooms: string;
  bedrooms: string;
  onFilterChange: (beds: string, baths: string) => void;
  onFiltersButtonClick: () => void;
  onApplySavedSearch: (savedSearch: SavedSearchFieldsFragment) => void;
  /** Highlights the applied saved search in the switcher. */
  activeSavedSearchId?: string | null;
  /** Inventory-set switch (For sale / Available soon), right of saved searches. */
  inventoryControl?: React.ReactNode;
}

const COUNT_OPTIONS: ComboboxOption[] = [
  { label: '0', value: '' },
  { label: '1+', value: '1' },
  { label: '2+', value: '2' },
  { label: '3+', value: '3' },
  { label: '4+', value: '4' },
];

export const QuickFilters = ({
  bathrooms,
  bedrooms,
  onFilterChange,
  setSelectedAddress,
  selectedAddress,
  setSelectedLocation,
  selectedLocation,
  onFiltersButtonClick,
  isFocused,
  onApplySavedSearch,
  activeSavedSearchId,
  inventoryControl,
}: QuickFiltersProps & LocationSearchProps) => {
  const handleOnBedroomsChange = (value: string) => {
    onFilterChange(value, bathrooms);
  };

  const handleOnBathroomsChange = (value: string) => {
    onFilterChange(bedrooms, value);
  };

  const { isLoggedIn } = useSavedSearches();

  return (
    <Col className="w-full gap-3 py-6">
      <Row className="w-full gap-3">
        <Box className="min-w-0 flex-1 lg:[&_input]:text-body-sm">
          <MapLocationSearch
            isFocused={isFocused}
            selectedAddress={selectedAddress}
            setSelectedAddress={setSelectedAddress}
            setSelectedLocation={setSelectedLocation}
            selectedLocation={selectedLocation}
          />
        </Box>
        <Col className="hidden w-20 shrink-0 gap-3 lg:flex [&_input]:pr-8 [&_input]:text-body-sm">
          <Text as="label" className="text-label-md text-od-content-primary">
            Beds
          </Text>
          <Combobox
            aria-label="Beds"
            size="sm"
            placeholder="0"
            options={COUNT_OPTIONS}
            value={COUNT_OPTIONS.find((option) => option.value === (bedrooms || '')) ?? null}
            onValueChange={(option) => handleOnBedroomsChange(option?.value ?? '')}
          />
        </Col>
        <Col className="hidden w-20 shrink-0 gap-3 lg:flex [&_input]:pr-8 [&_input]:text-body-sm">
          <Text as="label" className="text-label-md text-od-content-primary">
            Baths
          </Text>
          <Combobox
            aria-label="Baths"
            size="sm"
            placeholder="0"
            options={COUNT_OPTIONS}
            value={COUNT_OPTIONS.find((option) => option.value === (bathrooms || '')) ?? null}
            onValueChange={(option) => handleOnBathroomsChange(option?.value ?? '')}
          />
        </Col>
      </Row>
      <Row className="w-full flex-wrap items-center justify-between gap-3">
        <Button
          analyticsName="filter-property-search"
          aria-label="All Filters"
          onClick={onFiltersButtonClick}
          variant="secondary"
        >
          All Filters
        </Button>
        <Row className="min-w-0 flex-wrap items-center gap-3">
          {isLoggedIn ? (
            <SavedSearchesDropdown
              onApplySavedSearch={onApplySavedSearch}
              activeSavedSearchId={activeSavedSearchId}
              onOpenFilters={onFiltersButtonClick}
            />
          ) : null}
          {inventoryControl}
        </Row>
      </Row>
    </Col>
  );
};
